import { useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Formatter } from 'recharts/types/component/DefaultLegendContent';
import { Container, ChartWrapper, EmptyState } from './index.styles';
import { useContainerHeight } from '../useContainerHeight';
import { resolveCategoryLabel } from '../../../../helpers';
import ProfileIcons from '../../../ProfileIcons';
import { PIE_CHART_COLORS, CHART_TICK_COLOR } from '../consts';
import { ChartTooltip } from '../TooltipComponent';

const PIE_COLORS = PIE_CHART_COLORS;
const LEGEND_TEXT_COLOR = CHART_TICK_COLOR;

type IndexableDatum = NonNullable<PieModuleData['data']>[number] & Record<string, unknown>;

interface PieSlice {
  name: string;
  value: number;
  legendLabel: string;
  tooltipText: string;
}

type LegendEntry = { payload?: { legendLabel?: string } };

const renderLegendLabel = (value: string, entry: LegendEntry) => (
  <span style={{ color: LEGEND_TEXT_COLOR }}>{entry?.payload?.legendLabel ?? value}</span>
);

export const PieChartRenderer = ({
  data,
  selectedProfiles,
  supportedPlatforms,
}: DynamicModuleBuilderProps & {
  selectedProfiles?: ReportProfile[];
  supportedPlatforms?: string[];
}) => {
  const { t } = useTranslation('reports');
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const chartHeight = useContainerHeight(chartWrapperRef as React.RefObject<HTMLDivElement>);

  if (!data || data.chartType !== 'Pie') {
    return <EmptyState>{t('new_reports_no_engagement_data')}</EmptyState>;
  }

  const moduleData = data as PieModuleData & {
    legend?: { dataKey?: string; name?: string };
  };

  const { metrics, data: pieData, options, legend } = moduleData;

  if (!pieData || pieData.length === 0) {
    return <EmptyState>{t('new_reports_no_engagement_data')}</EmptyState>;
  }

  const rows = pieData as IndexableDatum[];

  const categoryKey = legend?.dataKey ?? 'category';
  const isNumberWithPercentage = metrics[0]?.type === 'numberWithPercentage';
  const displayNumber = options?.display === 'number';
  const showNumber = isNumberWithPercentage && displayNumber;

  const chartData: PieSlice[] = rows.map((entry) => {
    const name = resolveCategoryLabel(entry[categoryKey] as string, t);
    const rawValue = entry.value;

    if (typeof rawValue === 'object' && rawValue !== null && 'percentage' in rawValue) {
      const pct = rawValue.percentage;

      return {
        name,
        value: pct,
        legendLabel: showNumber ? `${name} - ${rawValue.number.toLocaleString()}` : `${name} - ${(pct * 100).toFixed(0)}%`,
        tooltipText: showNumber ? `${rawValue.number.toLocaleString()} (${(pct * 100).toFixed(1)}%)` : `${(pct * 100).toFixed(1)}%`,
      };
    }

    const pct = Number(rawValue);

    return {
      name,
      value: pct,
      legendLabel: `${name} - ${(pct * 100).toFixed(0)}%`,
      tooltipText: `${(pct * 100).toFixed(1)}%`,
    };
  });

  return (
    <Container>
      <ChartWrapper ref={chartWrapperRef}>
        <ResponsiveContainer
          width="100%"
          height={chartHeight || '100%'}
        >
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="40%"
              outerRadius="70%"
              paddingAngle={0}
              isAnimationActive={false}
            >
              {chartData.map((entry, idx) => (
                <Cell
                  key={entry.name}
                  fill={PIE_COLORS[idx % PIE_COLORS.length]}
                  stroke="none"
                />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip variant="pie" />} />
            <Legend
              align="left"
              iconType="circle"
              iconSize={12}
              wrapperStyle={{ fontSize: '12px', bottom: '3px' }}
              formatter={renderLegendLabel as Formatter}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartWrapper>
      <ProfileIcons
        isChartType
        selectedProfiles={selectedProfiles}
        supportedPlatforms={supportedPlatforms}
      />
    </Container>
  );
};
